import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { ai, fonts as fontsApi } from '../api/client.js';

export default function LicenseSummary({ fontId, license, licenseType }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const hasLicense = license || (licenseType && licenseType !== 'unknown');

  // Reset cached data when font changes
  useEffect(() => {
    setData(null);
    setExpanded(false);
  }, [fontId]);

  useEffect(() => {
    if (!hasLicense || !expanded || data) return;
    setLoading(true);
    ai.licenseSummary(fontId)
      .then(setData)
      .catch(() => setData({ summary: 'Unable to analyze license.', allowed: [], restricted: [] }))
      .finally(() => setLoading(false));
  }, [fontId, expanded, data, hasLicense]);

  if (!hasLicense) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: expanded ? 8 : 0, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}
      >
        <span style={{ fontSize: 11, color: theme.accent }}>&#10024;</span>
        license summary
        <span style={{ fontSize: 8, color: theme.textGhost, marginLeft: 'auto' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div style={{
          background: 'transparent',
          border: `1px solid ${theme.border}`,
          borderRadius: 0, padding: 12,
        }}>
          {loading ? (
            <div style={{ fontSize: 11, color: theme.textMuted }}>analyzing license…</div>
          ) : data ? (
            <>
              <div style={{ fontSize: 11, color: theme.textSecondary, lineHeight: 1.5, marginBottom: 8 }}>
                {data.summary}
              </div>
              {data.pdf_available && (
                <button
                  onClick={async () => {
                    setPdfDownloading(true);
                    try { await fontsApi.downloadLicensePdf(fontId); }
                    catch (e) { alert('Could not download license PDF.'); }
                    finally { setPdfDownloading(false); }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', marginBottom: 8,
                    background: theme.accentBg, border: `1px solid ${theme.accentBorder}`,
                    borderRadius: 0, color: theme.accent,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 13 }}>⬇</span>
                  {pdfDownloading ? 'downloading…' : 'download license pdf'}
                </button>
              )}
              {data.allowed?.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: theme.green, marginBottom: 3, textTransform: 'uppercase' }}>allowed</div>
                  {data.allowed.map((item, i) => (
                    <div key={i} style={{ fontSize: 10, color: theme.textTertiary, padding: '1px 0', display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ color: theme.green, fontSize: 8 }}>&#10003;</span> {item}
                    </div>
                  ))}
                </div>
              )}
              {data.restricted?.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: theme.red, marginBottom: 3, textTransform: 'uppercase' }}>restricted</div>
                  {data.restricted.map((item, i) => (
                    <div key={i} style={{ fontSize: 10, color: theme.textTertiary, padding: '1px 0', display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ color: theme.red, fontSize: 8 }}>&#10007;</span> {item}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
